
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  Terminal, 
  Play, 
  Square, 
  Trash2, 
  Download,
  RefreshCw,
  Database,
  Users,
  CheckCircle2,
  UserCheck
} from "lucide-react";
import { useAffiliateBackfill } from '@/hooks/useAffiliateBackfill';
import { cn } from '@/lib/utils';

export const AffiliateBackfillConsole = () => {
  const {
    logs,
    isRunning,
    progress,
    runFullMigration,
    runUserIdBackfillOnly,
    runSyncOnly,
    runBackfillOnly,
    runValidationOnly,
    clearLogs,
    exportLogs
  } = useAffiliateBackfill();

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const getLogClassName = (type: string) => {
    switch (type) {
      case 'error':
        return 'text-red-400';
      case 'warning':
        return 'text-yellow-400';
      case 'success':
        return 'text-green-400';
      case 'info':
        return 'text-blue-400';
      default:
        return 'text-muted-foreground';
    }
  };

  const getLogIcon = (type: string) => {
    switch (type) {
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'success':
        return '✅';
      case 'info':
        return 'ℹ️';
      default:
        return '📝';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            <CardTitle>Backfill Console</CardTitle>
            {isRunning && (
              <Badge variant="secondary" className="animate-pulse">
                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                Running...
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoScroll(!autoScroll)}
              className={cn(autoScroll && "bg-primary/10")}
            >
              Auto Scroll
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={clearLogs}
              disabled={isRunning}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportLogs}
              disabled={logs.length === 0}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <Button
            onClick={runFullMigration}
            disabled={isRunning}
            className="h-auto p-4 flex-col items-start"
          >
            <div className="flex items-center gap-2 mb-1">
              <Database className="h-4 w-4" />
              <span className="font-medium">Full Migration</span>
            </div>
            <span className="text-xs opacity-75">
              All steps: User ID + Sync + Backfill + Validate
            </span>
          </Button>

          <Button
            variant="outline"
            onClick={runUserIdBackfillOnly}
            disabled={isRunning}
            className="h-auto p-4 flex-col items-start"
          >
            <div className="flex items-center gap-2 mb-1">
              <UserCheck className="h-4 w-4" />
              <span className="font-medium">User ID Backfill</span>
            </div>
            <span className="text-xs opacity-75">
              Backfill affiliates.user_id
            </span>
          </Button>

          <Button
            variant="outline"
            onClick={runSyncOnly}
            disabled={isRunning}
            className="h-auto p-4 flex-col items-start"
          >
            <div className="flex items-center gap-2 mb-1">
              <RefreshCw className="h-4 w-4" />
              <span className="font-medium">Sync Only</span>
            </div>
            <span className="text-xs opacity-75">
              Sync referred_by_affiliate
            </span>
          </Button>

          <Button
            variant="outline"
            onClick={runBackfillOnly}
            disabled={isRunning}
            className="h-auto p-4 flex-col items-start"
          >
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4" />
              <span className="font-medium">Backfill Only</span>
            </div>
            <span className="text-xs opacity-75">
              Create missing limits
            </span>
          </Button>

          <Button
            variant="outline"
            onClick={runValidationOnly}
            disabled={isRunning}
            className="h-auto p-4 flex-col items-start"
          >
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="h-4 w-4" />
              <span className="font-medium">Validate Only</span>
            </div>
            <span className="text-xs opacity-75">
              Check consistency
            </span>
          </Button>
        </div>

        {/* Progress Bar */}
        {progress > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-secondary rounded-full h-2">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Console Output */}
        <div className="border rounded-md bg-black/95">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/20">
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <span className="text-sm text-muted-foreground">Console Output</span>
            </div>
            <Badge variant="outline" className="text-xs">
              {logs.length} lines
            </Badge>
          </div>
          
          <ScrollArea 
            className="h-[400px] p-4" 
            ref={scrollAreaRef}
          >
            {logs.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <Terminal className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Console output will appear here...</p>
                <p className="text-xs mt-1">Click any action button to start</p>
              </div>
            ) : (
              <div className="space-y-1 font-mono text-sm">
                {logs.map((log, index) => (
                  <div 
                    key={index} 
                    className={cn(
                      "flex items-start gap-2 leading-relaxed",
                      getLogClassName(log.type)
                    )}
                  >
                    <span className="flex-shrink-0 w-6 text-center">
                      {getLogIcon(log.type)}
                    </span>
                    <span className="opacity-60 flex-shrink-0 text-xs">
                      {log.timestamp}
                    </span>
                    <span className="flex-1 break-words">
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
};
